import { Group, Menu, Portal, Text, Tooltip } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { IconArrowDown, IconArrowUpRight, IconCloudShare } from '@tabler/icons-react';
import {
  IconBackspace,
  IconCloudDown,
  IconCopy,
  IconDotsVertical,
  IconEye,
  IconFileEuro,
  IconForms,
  IconLicense,
  IconMapCheck,
  IconPencil,
  IconPlus,
  IconSettingsExclamation,
  IconTrashX,
  IconQrcode,
} from '@tabler/icons-react';
import { useApi } from 'api/api-context';
import ActionIcon from 'components/ActionIcon';
import Button from 'components/Button';
import { DeviceAdminSetRemoteSourceResponse } from 'api/actions/device-admin-set-remote-source/device-admin-set-remote-source-response';
import { useChangeDeviceRemoteSourceModal } from 'components/modals/change-device-remote-source/ChangeDeviceRemoteSourceModalProvider';
import { useAcknowledge, useConfirm } from 'components/modals/message/MessageProvider';
import { useResponsiveDesign } from 'hooks/use-responsive-design';
import { FEATURE_TOGGLE_COPY_DEVICE } from 'env';
import { FEATURE_TOGGLE_DEVICE_QR_CODES } from 'env';
import { DeviceQrPairResponse } from 'api/actions/device-qr-pair/device-qr-pair-response';
import { useDeviceQrPairingModal } from 'components/modals/device-qr-pairing/DeviceQrPairingModalProvider';
import panic from 'errors/panic';
import { ROLE_ADMIN_ID } from 'model/Role';
import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ADD_FORM_PAGE_PATH,
  ADD_REVISION_PAGE_PATH,
  COPY_DEVICE_PAGE_PATH,
  DEVICES_PAGE_PATH,
  DEVICE_FAULTS_PAGE_PATH,
  EDIT_DEVICE_PAGE_PATH,
  FORMS_PAGE_PATH,
  ADD_PRICE_OFFER_PAGE_PATH,
  PRICE_OFFERS_PAGE_PATH,
  REDIRECT_PAGE_PATH,
  REVISIONS_PAGE_PATH,
} from 'routes/paths';
import { SUCCESS_NOTIFICATION_COLOR } from 'utils/constants';
import useOrganizationModules from 'features/organization-modules/use-organization-modules';

/**
 * Parameters of the DeviceActions component.
 */
export interface DeviceActionsProps {
  deviceId: number;
  deviceName: string;
  structure: string;
  parentDevice?: { deviceId: number; deviceName: string };
  remoteSource?: { remoteSourceId: number; type: string };
  externalId?: string;
  qrId?: string;
  organization: { organizationId: number };
  department: { departmentId: number };
  deviceType: { deviceTypeId: number };
  canDiscard: boolean;
  canEdit: boolean;
  canAssignRevisions: boolean;
  canAssignForms: boolean;
  canAssignPriceOffers: boolean;
  canSeeRevisions: boolean;
  canSeeForms: boolean;
  status: boolean;
  latitude?: number;
  longitude?: number;
  compactOnTablet?: boolean;
  onStatusChange: (status: boolean) => void;
  onRemoteSourceChange: (response: DeviceAdminSetRemoteSourceResponse) => void;
  onQrPairingChange: (response: DeviceQrPairResponse) => void;
}

/**
 * Displays actions for a device in a table
 */
export default function DeviceActions({
  deviceId,
  deviceName,
  structure,
  parentDevice,
  remoteSource,
  externalId,
  qrId,
  organization: { organizationId },
  department: { departmentId },
  deviceType: { deviceTypeId },
  canDiscard,
  canEdit,
  canAssignRevisions,
  canAssignForms,
  canAssignPriceOffers,
  canSeeRevisions,
  canSeeForms,
  latitude,
  longitude,
  status,
  compactOnTablet = false,
  onStatusChange,
  onRemoteSourceChange,
  onQrPairingChange,
}: DeviceActionsProps) {
  const { getAction, roleId } = useApi();
  const { openChangeDeviceRemoteSourceModal } = useChangeDeviceRemoteSourceModal();
  const { openDeviceQrPairingModal } = useDeviceQrPairingModal();
  const { confirm } = useConfirm();
  const { acknowledge } = useAcknowledge();
  const { isMobile, tableActionMode } = useResponsiveDesign();
  const { priceOffers, revisions, forms } = useOrganizationModules();
  const isCompact = compactOnTablet && tableActionMode !== 'desktop';
  const menuOnly = compactOnTablet && tableActionMode === 'mobile';
  const isAdmin = roleId === ROLE_ADMIN_ID;

  const [syncing, setSyncing] = useState(false);

  const addRevisionPageLink = useMemo(() => {
    const initialDevices = [{ deviceId }];
    const devicesParam = encodeURIComponent(JSON.stringify(initialDevices));

    const query = [
      `revisionName=${encodeURIComponent(deviceName)}`,
      `organizationId=${organizationId}`,
      `departmentId=${departmentId}`,
      `deviceTypeId=${deviceTypeId}`,
      `devices=${devicesParam}`,
    ].join('&');

    return `${ADD_REVISION_PAGE_PATH.original}?${query}`;
  }, [deviceId, deviceName, organizationId, departmentId, deviceTypeId]);

  const addFormPageLink = useMemo(() => {
    const initialDevices = [{ deviceId }];
    const devicesParam = encodeURIComponent(JSON.stringify(initialDevices));

    const query = [
      `revisionName=${encodeURIComponent(deviceName)}`,
      `organizationId=${organizationId}`,
      `departmentId=${departmentId}`,
      `deviceTypeId=${deviceTypeId}`,
      `devices=${devicesParam}`,
    ].join('&');

    return `${ADD_FORM_PAGE_PATH.original}?${query}`;
  }, [deviceId, deviceName, organizationId, departmentId, deviceTypeId]);

  const addPriceOfferPageLink = useMemo(() => {
    return `${ADD_PRICE_OFFER_PAGE_PATH.original}?deviceId=${deviceId}`;
  }, [deviceId]);

  const priceOffersPageLink = useMemo(() => {
    return `${PRICE_OFFERS_PAGE_PATH.original}?deviceId=${deviceId}`;
  }, [deviceId]);

  /**
   * Makes a call to the API to set the status of the device.
   */
  const setStatusRemote = useCallback(
    (status: boolean) => {
      const deviceSetStatusAction = getAction('DeviceSetStatus');

      deviceSetStatusAction({ parameters: { deviceId: String(deviceId) }, payload: { status } })
        .then(() => onStatusChange(status))
        .catch(panic);
    },
    [getAction, deviceId]
  );

  /**
   * Confirms the status change.
   */
  const confirmSetStatus = useCallback(
    (status: boolean) => {
      if (!status && !canDiscard) {
        acknowledge({
          title: 'Nemožno vyradiť zariadenie',
          content:
            'Existujú rozpracované revízne správy týkajúce sa zariadenia. Ukončite rozpracované revízne správy a potom môžete zariadenie vyradiť.',
        });
      } else {
        const title = status ? 'Zaradiť zariadenie' : 'Vyradiť zariadenie';
        const message = status ? 'Naozaj chcete zaradiť zariadenie?' : 'Naozaj chcete vyradiť zariadenie?';

        confirm({
          title,
          content: message,
          onConfirm: () => setStatusRemote(status),
        });
      }
    },
    [confirm, setStatusRemote]
  );

  /**
   * Syncs device with remote source.
   */
  const syncDevice = useCallback(() => {
    confirm({
      title: 'Synchronizovať zariadenie',
      content: 'Naozaj chcete synchronizovať zariadenie so vzdialeným zdrojom?',
      onConfirm: async () => {
        try {
          setSyncing(true);

          const syncAction = getAction('RemoteSourceSyncDevice');

          await syncAction({
            parameters: { remoteSourceId: String(remoteSource!.remoteSourceId), deviceRemoteId: externalId! },
          });

          showNotification({
            title: 'Synchronizácia dokončená',
            message: 'Zariadenie bolo úspešne synchronizované so vzdialeným zdrojom.',
            color: SUCCESS_NOTIFICATION_COLOR,
          });
        } catch (error: any) {
          panic(error);
        } finally {
          setSyncing(false);
        }
      },
    });
  }, [confirm, acknowledge, getAction, remoteSource, externalId]);

  /**
   * Applies the updated remote source values from the admin modal.
   */
  const handleRemoteSourceChange = useCallback(
    (response: DeviceAdminSetRemoteSourceResponse) => {
      onRemoteSourceChange(response);
    },
    [onRemoteSourceChange]
  );

  /**
   * Opens the admin-only remote source change modal.
   */
  const openChangeRemoteSource = useCallback(() => {
    openChangeDeviceRemoteSourceModal({
      deviceId,
      deviceName,
      remoteSourceId: remoteSource?.remoteSourceId,
      externalId,
      onRemoteSourceChange: handleRemoteSourceChange,
    });
  }, [
    deviceId,
    deviceName,
    externalId,
    handleRemoteSourceChange,
    openChangeDeviceRemoteSourceModal,
    remoteSource?.remoteSourceId,
  ]);

  /** Opens the QR scanner and applies a successful pairing to this row. */
  const openQrPairing = useCallback(() => {
    openDeviceQrPairingModal({ mode: 'pair', deviceId, deviceName, onPaired: onQrPairingChange });
  }, [deviceId, deviceName, onQrPairingChange, openDeviceQrPairingModal]);

  const mainAction = useMemo(() => {
    if (menuOnly) {
      return null;
    }

    if (isCompact) {
      const label = canEdit ? 'Editovať' : 'Zobraziť';
      const icon = canEdit ? <IconPencil /> : <IconEye />;
      return (
        <Tooltip label={label} events={{ hover: true, focus: true, touch: true }}>
          <ActionIcon
            variant="secondary"
            size="md"
            component={Link}
            to={EDIT_DEVICE_PAGE_PATH.insert({ deviceId })}
            disabled={!status}
          >
            {icon}
          </ActionIcon>
        </Tooltip>
      );
    }

    return (
      <Button
        w={120}
        size="md"
        component={Link}
        to={EDIT_DEVICE_PAGE_PATH.insert({ deviceId })}
        variant="secondary"
        leftIcon={canEdit ? <IconPencil /> : <IconEye />}
        disabled={!status}
      >
        {canEdit ? 'Editovať' : 'Zobraziť'}
      </Button>
    );
  }, [canEdit, deviceId, isCompact, menuOnly, status]);

  return (
    <Group spacing={12} noWrap>
      {mainAction}
      <Menu position="bottom-end">
        <Menu.Target>
          <ActionIcon variant="tertiary" size={isMobile ? 'sm' : 'md'}>
            <IconDotsVertical stroke="1.5" />
          </ActionIcon>
        </Menu.Target>
        <Portal>
          <Menu.Dropdown>
            <Menu.Label>
              <Text maw={160} truncate>
                {deviceName}
              </Text>
            </Menu.Label>
            {menuOnly &&
              (status ? (
                <Menu.Item
                  component={Link}
                  to={EDIT_DEVICE_PAGE_PATH.insert({ deviceId })}
                  icon={canEdit ? <IconPencil stroke={1.5} size={20} /> : <IconEye stroke={1.5} size={20} />}
                >
                  {canEdit ? 'Editovať' : 'Zobraziť'}
                </Menu.Item>
              ) : (
                <Menu.Item
                  icon={canEdit ? <IconPencil stroke={1.5} size={20} /> : <IconEye stroke={1.5} size={20} />}
                  disabled
                >
                  {canEdit ? 'Editovať' : 'Zobraziť'}
                </Menu.Item>
              ))}
            {(FEATURE_TOGGLE_COPY_DEVICE && canEdit) ||
            isAdmin ||
            (canEdit && remoteSource && externalId && remoteSource.type === 'chastia') ? (
              <>
                {FEATURE_TOGGLE_COPY_DEVICE && canEdit && (
                  <Menu.Item
                    color="gray.8"
                    component={Link}
                    to={COPY_DEVICE_PAGE_PATH.insert({ deviceId })}
                    icon={<IconCopy stroke={1.5} size={20} />}
                  >
                    Kopírovať
                  </Menu.Item>
                )}
                {isAdmin && (
                  <Menu.Item
                    color="gray.8"
                    onClick={openChangeRemoteSource}
                    icon={<IconCloudShare stroke={1.5} size={20} />}
                  >
                    Zmeniť externý zdroj
                  </Menu.Item>
                )}
                {canEdit && remoteSource && externalId && remoteSource.type === 'chastia' && (
                  <Menu.Item
                    onClick={syncDevice}
                    icon={<IconCloudDown stroke={1.5} size={20} />}
                    color="gray.8"
                    disabled={syncing || !status}
                  >
                    {syncing ? 'Synchronizujem...' : 'Synchronizovať'}
                  </Menu.Item>
                )}
                <Menu.Divider />
              </>
            ) : null}
            {status && (
              <>
                {parentDevice && (
                  <Menu.Item
                    component={Link}
                    to={EDIT_DEVICE_PAGE_PATH.insert({ deviceId: parentDevice.deviceId })}
                    icon={<IconArrowUpRight stroke="1.5" size={20} />}
                    color="gray.8"
                  >
                    Zobraziť nadradené zariadenie
                  </Menu.Item>
                )}
                {structure === 'parent' && (
                  <Menu.Item
                    component={Link}
                    to={`${REDIRECT_PAGE_PATH.original}?to=${encodeURIComponent(
                      `${DEVICES_PAGE_PATH.original}?parentDeviceId=${deviceId}`
                    )}`}
                    icon={<IconArrowDown stroke="1.5" size={20} />}
                    color="gray.8"
                  >
                    Zobraziť podradené zariadenia
                  </Menu.Item>
                )}
                <Menu.Item
                  component={Link}
                  to={DEVICE_FAULTS_PAGE_PATH.insert({ deviceId })}
                  icon={<IconSettingsExclamation stroke="1.5" size={20} />}
                  color="gray.8"
                >
                  Zobraziť nedostatky
                </Menu.Item>
              </>
            )}
            {FEATURE_TOGGLE_DEVICE_QR_CODES && canEdit && (
              <>
                <Menu.Divider />
                <Menu.Item onClick={openQrPairing} icon={<IconQrcode stroke={1.5} size={20} />} color="gray.8">
                  {qrId ? 'Zmeniť QR kód' : 'Párovať s QR kódom'}
                </Menu.Item>
              </>
            )}
            {longitude && latitude && (
              <Menu.Item
                component={Link}
                to={`https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`}
                icon={<IconMapCheck stroke={1.5} size={20} />}
                target="_blank"
              >
                Zobraziť na mape
              </Menu.Item>
            )}
            {revisions && canSeeRevisions && (
              <>
                <Menu.Divider />
                <Menu.Item
                  component={Link}
                  to={`${REVISIONS_PAGE_PATH.original}?deviceId=${deviceId}`}
                  icon={<IconLicense stroke={1.5} size={20} />}
                >
                  Zobraziť revízne správy
                </Menu.Item>
              </>
            )}
            {revisions && status && canAssignRevisions && (
              <Menu.Item
                component={Link}
                to={addRevisionPageLink}
                icon={<IconPlus stroke={1.5} size={20} />}
                color="gray.8"
              >
                Vytvoriť revíznu správu
              </Menu.Item>
            )}
            {forms && canSeeForms && (
              <>
                <Menu.Divider />
                <Menu.Item
                  component={Link}
                  to={`${FORMS_PAGE_PATH.original}?deviceId=${deviceId}`}
                  icon={<IconForms stroke={1.5} size={20} />}
                >
                  Zobraziť formuláre
                </Menu.Item>
              </>
            )}
            {forms && status && canAssignForms && (
              <Menu.Item
                component={Link}
                to={addFormPageLink}
                icon={<IconPlus stroke={1.5} size={20} />}
                color="gray.8"
              >
                Vytvoriť formulár
              </Menu.Item>
            )}
            {priceOffers && canAssignPriceOffers && (
              <>
                <Menu.Divider />
                <Menu.Item
                  component={Link}
                  to={priceOffersPageLink}
                  icon={<IconFileEuro stroke={1.5} size={20} />}
                  color="gray.8"
                >
                  Zobraziť cenové ponuky
                </Menu.Item>
                {status && (
                  <Menu.Item
                    component={Link}
                    to={addPriceOfferPageLink}
                    icon={<IconPlus stroke={1.5} size={20} />}
                    color="gray.8"
                  >
                    Zadať cenovú ponuku
                  </Menu.Item>
                )}
              </>
            )}
            {canEdit && (
              <>
                <Menu.Divider />
                <Menu.Item
                  color={status ? 'red.8' : 'red.8'}
                  onClick={() => confirmSetStatus(!status)}
                  icon={status ? <IconTrashX stroke={1.5} size={20} /> : <IconBackspace stroke={1.5} size={20} />}
                >
                  {status ? 'Vyradiť' : 'Zaradiť'}
                </Menu.Item>
              </>
            )}
          </Menu.Dropdown>
        </Portal>
      </Menu>
    </Group>
  );
}
